import { useMemo, useState } from "react";
import { openSnackbarAction } from "@icarius-common/snackbar/actions";
import { getLocalizedString } from "@icarius-localization/strings";
import { useDispatch } from "react-redux";

const MAX_LENGTH_LONG = 250;

const useHandleForm = (data, modifyCallback) => {

  const extractFormData = (dataInput) => {
    const auxValues = {};

    if (dataInput) {
      Object.keys(dataInput).forEach((section) => {
        const sectionData = dataInput[section];
        if (!Array.isArray(sectionData)) return;

        const columns = Array.isArray(sectionData[0]) ? sectionData : [sectionData];

        columns.forEach((column) => {
          if (!Array.isArray(column)) return;
          column.forEach((item) => {
            if (!item || typeof item !== "object") return;
            const fieldName = Object.keys(item)[0];
            const fieldObj = item[fieldName];

            if (fieldObj?.multiple) {
              auxValues[fieldName] = Array.isArray(fieldObj.value)
                ? fieldObj.value
                : (fieldObj?.value !== undefined && fieldObj?.value !== null && fieldObj?.value !== "" ? [fieldObj.value] : []);
            } else if (fieldObj?.["value"] !== undefined) {
              auxValues[fieldName] = fieldObj["value"];
            }
          });
        });
      });
    }

    return auxValues;
  };

  const [formData, setFormData] = useState(() => extractFormData(data));

  const IMAGE_FIELDS = useMemo(() => {
    return Object.values(data)
      .flat(2)
      .filter((item) => item && typeof item === "object" && Object.keys(item)?.length && Object.values(item)[0]?.type === "image")
      .map((item) => Object.keys(item)[0]);
  }, [data]);

  const TEXT_FIELDS = useMemo(() => {
    return Object.values(data)
      .flat(2)
      .filter((item) => item && typeof item === "object" && Object.keys(item)?.length && Object.values(item)[0]?.type === "text")
      .map((item) => Object.keys(item)[0]);
  }, [data]);

  const INTEGER_FIELDS = useMemo(() => {
    return Object.values(data)
      .flat(2)
      .filter((item) => item && typeof item === "object" && Object.keys(item)?.length && Object.values(item)[0]?.type === "integer")
      .map((item) => Object.keys(item)[0]);
  }, [data]);

  const MULTIPLE_FIELDS = useMemo(() => {
    return Object.values(data)
      .flat(2)
      .filter((item) => item && typeof item === "object" && Object.keys(item)?.length && Boolean(Object.values(item)[0]?.multiple))
      .map((item) => Object.keys(item)[0]);
  }, [data]);

  const dispatch = useDispatch();

  const fileIsValidImage = (file) => {
    return (
      file.type.includes("/png") ||
      file.type.includes("/jpg") ||
      file.type.includes("/jpeg") ||
      file.type.includes("/bmp") ||
      file.type.includes("/gif")
    )
  }

  const valuesAreValid = () => {
    let errorMessage = "";
    if (!(formData["CLIENTE"].length > 0 && formData["CLIENTE"].length <= MAX_LENGTH_LONG)) {
      errorMessage = getLocalizedString("fillClientGeneralSettings")
    }

    if (formData["STOP"] === true) {
      if (!(formData["STOPMSG"].length > 0 && formData["STOPMSG"].length <= MAX_LENGTH_LONG)) {
        errorMessage = getLocalizedString("fillStopMsgGeneralSettings")
      }

      if (!(formData["STOPTIME"].length > 0 && formData["STOPTIME"].length <= MAX_LENGTH_LONG)) {
        errorMessage = getLocalizedString("fillStopTimeGeneralSettings")
      }
    }

    if (!(formData["EMAILRRHH"].length > 0 && formData["EMAILRRHH"].length <= MAX_LENGTH_LONG)) {
      errorMessage = getLocalizedString("fillHREmailGeneralSettings")
    }

    if (!(formData["GO_DTO_RRHH"].length > 0 && formData["GO_DTO_RRHH"].length <= MAX_LENGTH_LONG)) {
      errorMessage = getLocalizedString("fillHRDTOGeneralSettings")
    }

    if (!(formData["GO_EMAIL"].length > 0 && formData["GO_EMAIL"].length <= MAX_LENGTH_LONG)) {
      errorMessage = getLocalizedString("fillContactEmailGeneralSettings")
    }

    const PORMAXANT_regex = /^((100)|(\d{1,2}))%?$/;
    if (!(formData["PORMAXANT"].length > 0 && formData["PORMAXANT"].length <= 4 && PORMAXANT_regex.test(formData["PORMAXANT"]))) {
      errorMessage = getLocalizedString("fillMaxPercentageAdvance")
    }

    if (formData["FIRMADIGITAL"] === true) {
      if (formData["DISCLAMER"].length === 0) {
        errorMessage = getLocalizedString("fillDisclaimerGeneralSettings")
      }
      if (!(formData["FIRMACOL"].length > 0 && formData["FIRMACOL"].length <= MAX_LENGTH_LONG)) {
        errorMessage = getLocalizedString("fillFirmaColGeneralSettings")
      }
    }

    if (formData["GEO_DIVISION1"].length === 0) {
      errorMessage = getLocalizedString("firstLevelOfGeographicalOrganizationIsRequired")
    }

    if (formData["GEO_DIVISION2"].length === 0) {
      errorMessage = getLocalizedString("secondLevelGeographicOrganizationIsMandatory")
    }

    if (!(formData["GEO_DIVISION1"].length > 0 && formData["GEO_DIVISION1"].length <= MAX_LENGTH_LONG)) {
      errorMessage = getLocalizedString("verifyFirstLevelOfGeographicalOrganization")
    }

    if (!(formData["GEO_DIVISION2"].length > 0 && formData["GEO_DIVISION2"].length <= MAX_LENGTH_LONG)) {
      errorMessage = getLocalizedString("checkSecondLevelOfGeographicOrganization")
    }

    if (formData["GEO_DIVISION3"]?.length > 0 && formData["GEO_DIVISION3"]?.length >= MAX_LENGTH_LONG) {
      errorMessage = getLocalizedString("checkThirdLevelOfGeographicalOrganization")
    }

    if (errorMessage === "") return true;

    dispatch(openSnackbarAction({ msg: errorMessage, severity: "error" }));
    return false;
  };

  const submit = () => {
    let dataToSend = {};

    if (data) {
      Object.keys(data).forEach((key) => {
        const sectionData = data[key];
        if (!Array.isArray(sectionData)) return;

        const columns = Array.isArray(sectionData[0]) ? sectionData : [sectionData];

        columns.forEach((column) => {
          if (!Array.isArray(column)) return;
          column.forEach((item) => {
            if (!item || typeof item !== "object") return;
            const propName = Object.keys(item)[0];
            if (MULTIPLE_FIELDS.includes(propName)) {
              dataToSend[propName] = Array.isArray(formData[propName]) ? formData[propName] : [];
            } else {
              dataToSend[propName] = formData[propName] || "";
            }
          });
        });
      });
    }

    if (valuesAreValid(dataToSend)) {
      modifyCallback(dataToSend);
    }
  };

  const setFormValue = (value, fieldName) => {
    if (IMAGE_FIELDS.includes(fieldName)) {
      const { originalFile, og64 } = value;

      if (originalFile.size > 5 * 1024 * 1024) {
        dispatch(openSnackbarAction({ msg: getLocalizedString("imageSizeExceeded"), severity: "error" }));
        return;
      }

      if (!fileIsValidImage(originalFile)) {
        dispatch(openSnackbarAction({ msg: getLocalizedString("fileMustBeImage"), severity: "error" }));
        return;
      }

      setFormData({
        ...formData,
        [fieldName]: og64,
      });
      return;
    }

    if (INTEGER_FIELDS.includes(fieldName)) {
      setFormData({
        ...formData,
        [fieldName]: value.toString().slice(0, 3),
      });
      return;
    }

    if (TEXT_FIELDS.includes(fieldName)) {
      if (fieldName === "CLIENTE") {
        setFormData({
          ...formData,
          [fieldName]: value.toUpperCase(),
        });
        return;
      }

      setFormData({
        ...formData,
        [fieldName]: value.toString().slice(0, 511),
      });
      return;
    }

    if (MULTIPLE_FIELDS.includes(fieldName)) {
      setFormData({
        ...formData,
        [fieldName]: Array.isArray(value) ? value : (value !== undefined && value !== null && value !== "" ? [value] : []),
      });
      return;
    }

    setFormData({
      ...formData,
      [fieldName]: value,
    });
  };

  return {
    formData,
    setFormValue,
    submit,
  }
}

export default useHandleForm;
